import ngStorage from '../../common/wrappers/ngstorage';
import angularSpinner from '../../common/wrappers/angular-spinner';
import scrollServiceMod from '../common/scroll.service';
import serviceCategoryRes from '../common/resources/servicecategory.resource';
import serviceGroupRes from '../common/resources/servicegroup.resource';
import serviceRes from '../common/resources/service.resource';
import locationRes from '../common/resources/location.resource';
import geolocationModule from '../common/location/geolocation.service';
import flashMessageServiceMod  from '../../common/flashmessageservice/flashmessageservice.module';
import analyticsServiceMod from '../analytics/analytics.service';

import Fuse from 'fuse.js';

import personCardModule from './person-card.component';
import personRes from '../common/resources/person.resource';
import authServiceMod from '../auth/auth.service';
import personsCompactTpl from './persons-compact.tpl.html';
import personsFullTpl from './persons-full.tpl.html';
import jobTitleRes from '../common/resources/job-title.resource';
import {
  locationFilter,
  serviceFilter
} from './person-filters';
import personConstants from './persons.constants';
import {getServiceLanguage, SERVICE_LANGUAGES} from '../common/language.utils';
import selectorMod from '../../common/material-components/selects/select.component';
import _ from 'lodash';

import textfieldComponentMod from '../../common/material-components/textfields/textfield.component';
import normalButtonComponentMod from '../../common/material-components/buttons/normal-button.component';
import navButtonComponentMod from '../../common/material-components/buttons/nav-button.component';
import iconButtonComponentMod from '../../common/material-components/buttons/icon-button.component';
import PersonFilterState, { KEYS } from '../common/session-storage/person-filter-state';
import generalCardComponentMod from '../reservation/selections/general-card.component';
import jobtitleSelector from '../common/components/jobtitle-selector.component';

import './persons-widget.scss';
import {IS_MOBILE} from "../common/device-info.utils";
import {noop} from "../common/common.utils";
import {combineMatches, doSearch, getSearchTerms} from "../common/search";

/**
 * Display this many persons more when `show more` clicked.
 * @type {Number}
 */
const DISPLAY_ADD = 18;

/**
 * How many persons are displayed at max when normal limit is not used.
 * @type {number}
 */
const MAX_PERSONS = 150;

/**
 * Initially display at most this many persons.
 * @type {Number}
 */
const PERSON_CARD_PERSON_DETAIL_DELIMITER = ' • ';

const ORDER_ALPHABETICAL = Symbol('alphabetical');
const ORDER_POPULAR = Symbol('popular');
const ALL_PERSONNELL = 'Kaikki asiantuntijat';

/**
 * Regex for checking if the person search is about service voucher
 * @type {RegExp}
 */
const SERVICE_VOUCHER_SEARCH_REGEX = new RegExp(
  /(palvelu.*seteli|service.*voucher|service.*sedeln)/,
  'i'
);

const FUSE_CONFIG = {
  id: 'id',
  includeScore: true,
  includeMatches: true,
  threshold: 0.1,
  location: 0,
  distance: 10000,
  maxPatternLength: 32,
  minMatchCharLength: 3,
  keys: [
    {name: "last_name", weight: 0.9999},
    {name: "first_name", weight: 0.9998},
    {name: "locations_names", weight: 0.5},
    {name: "group_names", weight: 0.4},
    {name: "category_names", weight: 0.4},
    {name: "spoken_language_names", weight: 0.4},
    {name: "title", weight: 0.3},
    {name: "description", weight: 0.2},
  ]
};

/**
 * Standalone component to display filtered persons. This component can be used in at least
 * 3 different situations:
 * - Alone without any parameters when listing all personnel
 * - On location and service pages when providing service-id or location-id attributes.
 */
class PersonsComponent {
  constructor(
    $q,
    $log,
    $timeout,
    $httpParamSerializer,
    gettextCatalog,
    usSpinnerService,
    $sessionStorage,
    ScrollService,
    ServiceCategoryResource,
    ServiceGroupResource,
    ServiceResource,
    LocationResource,
    PersonResource,
    JobTitleResource,
    PersonConstants,
    AuthService,
    GeolocationService,
    FlashMessageService,
    Analytics,
  ) {
    'ngInject';

    this.$q = $q;
    this.$log = $log;
    this.$httpParamSerializer = $httpParamSerializer;
    this.$sessionStorage = $sessionStorage;
    this.$timeout = $timeout;
    this.usSpinnerService = usSpinnerService;
    this.ScrollService = ScrollService;
    this.ServiceCategoryResource = ServiceCategoryResource;
    this.ServiceGroupResource = ServiceGroupResource;
    this.ServiceResource = ServiceResource;
    this.LocationResource = LocationResource;
    this.PersonResource = PersonResource;
    this.JobTitleResource = JobTitleResource;
    this.PersonConstants = PersonConstants;
    this.AuthService = AuthService;
    this.GeolocationService = GeolocationService;
    this.FlashMessageService = FlashMessageService;
    this.Analytics = Analytics;
    this.gettextCatalog = gettextCatalog;
    this.filteredPersons = [];
    this.nonInternetPersons = [];
    this.personFilterState = new PersonFilterState($log, $sessionStorage);

    this.serviceCategoryFilter = '';
    this.serviceCategoryFilterOptions = [];

    this.serviceGroupFilter = '';
    this.serviceGroupFilterOptions = [];

    this.languageFilter = '';
    this.languageFilterOptions = [];

    this.locationFilterIds = [];
    this.locationIdsByCity = {};
    this.locationIdsByAreaId = {};

    this.compactMode = false;
    this.showInfo = false;
    this.personInfoCallback = null;

    /**
     * Hide filters when rendering full mode template.
     * @type {boolean}
     */
    this.hideFiltersInFullMode = false;

    /**
     * FuseJS search object; populated when data is received.
     * @type {Fuse<any, Fuse.FuseOptions<any>> | Fuse | null}
     */
    this.fuseSearch = null;

    /**
     * Persons retrieved from the backend for display.
     * @type {Array.<Person>}
     */
    this.persons = [];

    this.personSearchText = '';

    this.jobTitleFilter = '';

    this.jobTitleFilterOptions = [];

    /** Called when person is selected in the compact mode */
    this.personSelectedCallback = null;

    this.order = null;
    this.orderChoices = null;

    this.locationId = null;
    this.location = null;

    this.serviceId = null;
    this.service = null;

    /**
     * Are the persons ordered by distance and nearest branches.
     * @type {boolean}
     */
    this.distanceOrder = false;

    /**
     * Possible active person. Active person will be displayed in the template.
     * @type {Person}
     */
    this.activePerson = null;

    this.showMoreFilters = false;

    this._userNotify = user => $timeout(() => this._setUser(user));
    this.showFavourites = false;
    this.favouriteDoctors = [];
    this.loading = true;

    /**
     * Extra filters are not show by default on mobile.
     * @type {boolean}
     */
    this.showFilters = !IS_MOBILE;
  }

  $onInit() {
    this.AuthService.subscribeUser(this._userNotify);

    if (this.locationId == null) {
      this.showLocationSelector = true;
    }

    let setupPromises = [];
    if (this.locationId != null) {
      setupPromises.push(this.LocationResource.getOne(this.locationId).then(location => {
        this.location = location;
      }));
    }
    if (this.serviceId != null) {
      setupPromises.push(this.ServiceResource.getOne(this.serviceId).then(service => {
        this.service = service;
      }));
    }

    if (this.locationId != null || this.serviceId != null) {
      this.compactMode = true;
    }

    if (this.compactMode) {
      // Reset the state if we are in compact mode
      // This essentially means that filters are reset on location and service pages' person component
      this.personFilterState.reset();
    }
    this.showMoreFilters = this.personFilterState.get(KEYS.SHOW_FILTERS);

    this.orderChoices = [
      {label: this.gettextCatalog.getString('Suosituimmat'), obj: ORDER_POPULAR},
      {label: this.gettextCatalog.getString('Aakkosjärjestys'), obj: ORDER_ALPHABETICAL}
    ];
    this.order = this.orderChoices[1];

    this.loading = true;

    // Make sure all the data has been fetched before updating filters
    setupPromises.push(this.initData());
    this.$q.all(setupPromises).then(() => {
      if (this.location != null) {
        this.locationFilterIds = this.location.getIds();
      }
      else if (this.locationId != null) {
        this.locationFilterIds = [this.locationId];
      }
    })
    .catch(e => this.$log.warn("Failed to execute persons component setup.", e))
    .finally(() => {
      this.loading = false;
      this.updateFiltering();

      // Scroll to the previously selected person
      this.$timeout(
        () => {
          if (this.activePerson != null) {
            this.ScrollService.navigateToId(`person-${this.activePerson.id}`, 0, 0, 'persons-list');
          }
        },
        50
      );
    });

    this.languageFilter = this.languageFilterOptions[this.personFilterState.get(KEYS.LANGUAGE)];
  }

  $onDestroy() {
    this.AuthService.unsubscribeUser(this._userNotify);
  }

  initData() {
    this.loading = true;

    return this.$q.all([this.initPersons(), this.initLocations(), this.initFilterOptions()])
    .then(() => {
      this.persons = this.setDetailTexts(this.persons);
      this.fuseSearch = new Fuse(this.persons, FUSE_CONFIG);
      return this.initUser();
    })
    .catch((error) => {
      this.$log.error("Failed to load personnel: ", error);
    })
    .finally(() => {
      this.loading = false;
    });
  }

  initUser() {
    return this.AuthService.getUser()
    .then(u => this._setUser(u))
    .catch(noop);
  }

  initPersons() {
    this.persons = [];
    const params = {full: true};

    params.service = this.serviceId;

    return this.PersonResource.getMany(params)
    .then(data => {
      this.persons = data.list;
      return this.persons;
    });
  }

  initLocations() {
    return this.LocationResource.getMany({full: true})
    .then(locations => {
      const locationsByAreaId = _.groupBy(locations.list, location => location.location_area);
      for (const areaId in locationsByAreaId) {
        this.locationIdsByAreaId[areaId] = locationsByAreaId[areaId].map(location => location.id);
      }

      const locationsByCity = _.groupBy(locations.list, location => location.address.postal_area);
      for (const city in locationsByCity) {
        this.locationIdsByCity[city] = locationsByCity[city].map(location => location.id);
      }
    });
  }

  initFilterOptions() {
    return this.$q.all([
      this.initServiceCategoryFilterOptions(),
      this.initServiceGroupFilterOptions(),
      this.initJobTitleFilterOptions(),
      this.initLanguageFilterOptions(),
  ]);
  }

  initServiceCategoryFilterOptions() {
    this.serviceCategoryFilterOptions = [];

    return this.ServiceCategoryResource.getMany({full: true}).then(data => {
      for (const cat of data.list) {
        this.serviceCategoryFilterOptions.push({
          label: cat.name,
          obj: cat.id
        });
      }

      this.serviceCategoryFilterOptions.unshift({label: this.gettextCatalog.getString('Kaikki'), obj: null});
      this.serviceCategoryFilter = this.serviceCategoryFilterOptions[this.personFilterState.get(KEYS.SERVICE_CATEGORY)];
    });
  }

  initServiceGroupFilterOptions() {
    this.serviceGroupFilterOptions = [];

    return this.ServiceGroupResource.getMany({full: true}).then(data=> {
      for (const cat of data.list) {
        this.serviceGroupFilterOptions.push({
          label: cat.name,
          obj: cat.id
        });
      }

      this.serviceGroupFilterOptions.sort((l, r) => {
        return l.label.localeCompare(r.label, 'fi');
      });
      this.serviceGroupFilterOptions.unshift({label: this.gettextCatalog.getString('Kaikki'), obj: null});
      this.serviceGroupFilter = this.serviceGroupFilterOptions[this.personFilterState.get(KEYS.SERVICE_GROUP)];
    });

  }

  initJobTitleFilterOptions() {
    return this.JobTitleResource.getAll().then(jobTitles => {
      this.jobTitleFilterOptions = [{label: this.gettextCatalog.getString(ALL_PERSONNELL), obj: null}];
      for (const title of jobTitles.list) {
        this.jobTitleFilterOptions.push({
          label: this.PersonConstants.getJobTitlePlurals(title.type),
          obj: title.type
        });
      }
      this.jobTitleFilter = this.jobTitleFilterOptions[this.personFilterState.get(KEYS.JOB_TITLE)];
    });
  }

  initLanguageFilterOptions() {
    for (const language of SERVICE_LANGUAGES) {
      this.languageFilterOptions.push({
        label: language.in_lang_trans,
        obj: language.code,
      });
    }
    this.languageFilter = this.languageFilterOptions[this.personFilterState.get(KEYS.LANGUAGE)];

    return this.$q.when();
  }

  onOrderSet() {
    this.updateFiltering();
  }
  onCategoryFilterSet() {
    const idx = this.serviceCategoryFilterOptions.indexOf(this.serviceCategoryFilter);
    this.personFilterState.save(KEYS.SERVICE_CATEGORY, Math.max(0, idx));
    this.updateFiltering();
    this.Analytics.postEvent('asiantuntijat', 'filter', 'kategoria', this.serviceCategoryFilter ? this.serviceCategoryFilter.label : null);
  }

  onGroupFilterSet() {
    const idx = this.serviceGroupFilterOptions.indexOf(this.serviceGroupFilter);
    this.personFilterState.save(KEYS.SERVICE_GROUP, Math.max(0, idx));
    this.updateFiltering();
    this.Analytics.postEvent('asiantuntijat', 'filter', 'ryhmä', this.serviceGroupFilter ? this.serviceGroupFilter.label : null);
  }

  onLanguageFilterSet() {
    const idx = this.languageFilterOptions.indexOf(this.languageFilter);
    this.personFilterState.save(KEYS.LANGUAGE, Math.max(0, idx));
    this.updateFiltering();
    this.Analytics.postEvent('asiantuntijat', 'filter', 'kieli');
  }

  onAreaFilterSet(selection) {
    this.locationFilterIds = selection ? this.locationIdsByAreaId[selection.obj.id] : [];
    // Guard against the filter being called to early
    if (this.locationFilterIds == null) {
      this.locationFilterIds = [];
    }
    this.distanceOrder = false;
    this.updateFiltering();
    this.Analytics.postEvent('asiantuntijat', 'filter', 'alue-alue');
  }

  onCityFilterSet(selection) {
    this.locationFilterIds = selection ? this.locationIdsByCity[selection.obj] : [];
    if (this.locationFilterIds == null) {
      this.locationFilterIds = [];
    }
    this.distanceOrder = false;
    this.updateFiltering();
    this.Analytics.postEvent('asiantuntijat', 'filter', 'alue-kaupunki');
  }

  onLocationFilterSet(selection) {
    this.locationFilterIds = selection ? [selection.obj.id] : [];
    this.distanceOrder = false;
    this.updateFiltering();
    this.Analytics.postEvent('asiantuntijat', 'filter', 'alue-toimipiste');
  }

  showNearby() {
    this.loadingLocation = true;
    this.GeolocationService.getLocation()
      .catch(() => {
        this.$log.debug("Geolocation fetch failed");
      })
      .finally(() => {
        this.onLocationReceived();
        this.loadingLocation = false;
      });
    this.Analytics.postEvent('toimipisteet', 'paikannus', 'paikannus-nappi');
  }

  onLocationReceived() {
    this.Analytics.postEvent('asiantuntijat', 'filter', 'lähimmät');
    this.GeolocationService.getNearbyLocations(3).then(locations => {
        this.$log.debug("Found nearby locations", locations);

        this.locationFilterIds = locations.map(l => l.id);
        this.nearbyLocations = locations.map(l => l.name).join(PERSON_CARD_PERSON_DETAIL_DELIMITER);
        this.distanceOrder = true;

        this.updateFiltering();
      }
    ).catch((e) => {
      this.$log.debug("nearby error", e);
      this.FlashMessageService.addMessage(this.gettextCatalog.getString(
        'Emme voineet paikantaa sinua.'), 'error');
    });
  }

  onPersonSearchTextChanged() {
    this.updateSearches();
    if (this.personSearchText && this.personSearchText.length > 0) {
      this.Analytics.postEvent('asiantuntijat', 'filter', 'haku');
    }
  }

  onPersonSelected(person) {
    if (_.isFunction(this.personSelectedCallback)) {
      this.personSelectedCallback({person});
    }
  }

  onJobTitleFilterSet(jobtitle) {
    this.jobTitleFilter = jobtitle
    const idx = this.jobTitleFilterOptions.indexOf(this.jobTitleFilter);
    this.personFilterState.save(KEYS.JOB_TITLE, Math.max(0, idx));
    this.updateFiltering();
    this.Analytics.postEvent('asiantuntijat', 'filter', 'rooli');
  }

  /** Sets detail texts to given persons and return a list of new persons. */
  setDetailTexts(persons) {
    let ret = [];
    for (const person of persons) {
      let newPerson = person.copy();

      const delimiter = PERSON_CARD_PERSON_DETAIL_DELIMITER;

      // Languages
      newPerson.spoken_languages_text = '';
      const languages = [];
      for (const lang of person.spoken_languages) {
        try {
          languages.push(getServiceLanguage(this.gettextCatalog, lang));
        }
        catch (e) {
          this.$log.error("Skipping language setting for person", person, "language", lang);
        }
      }

      newPerson.spoken_language_names = languages;
      newPerson.spoken_languages_text = languages.join(delimiter);

      // Locations
      newPerson.locations_text = '';
      const locations = [];
      if (person.locations) {
        for (const locationId of person.locations) {
          const location = this.LocationResource.cache.getOneCached(locationId);
          if (location) {
            locations.push(location.name);
          }
        }
      }
      newPerson.locations_text = locations.join(delimiter); // There are usually commas in the location names, like "Helsinki, Itäkeskus"
      newPerson.locations_names = locations

      // Expertises is a combination of service groups and categories, groups first as there are less of them.
      const groups = [];
      const categories = [];
      for (const groupId of person.service_groups) {
        const group = this.ServiceGroupResource.cache.getOneCached(groupId);
        if (group) {
          groups.push(group.name);
        }
      }
      for (const categoryId of person.service_categories) {
        const category = this.ServiceCategoryResource.cache.getOneCached(categoryId);
        if (category) {
          categories.push(category.name);
        }
      }
      newPerson.group_names = groups;
      newPerson.category_names = categories;
      newPerson.expertises_text = groups.concat(categories).join(delimiter);

      ret.push(newPerson);
    }

    return ret;
  }

  updateFiltering() {
    // Allow for scoring of personnel based on other selected location and/or service
    const locationIsSet = this.locationFilterIds != null && this.locationFilterIds.length > 0;
    for (let p of this.persons) {
      p.hasLocation = locationIsSet ? p.locations.some(locId => this.locationFilterIds.includes(locId)): true;
      p.hasService = this.service != null ? this.service.internet_reservable && p.services.includes(this.service.id): true;
      // Don't get offline if user does not have service at all
      p.isOffline = this.service != null ? p.other_services.includes(this.service.id): false;

      // Generate mismatch errors
      p.mismatches = [];
      if (!p.hasService && !p.isOffline) {
        p.mismatches.push(this.gettextCatalog.getString('Asiantuntija ei tarjoa valittua palvelua <strong>"{{service}}"</strong>.', {
          service: this.service.name
        }))
      }
      if (!p.hasLocation) {
        p.mismatches.push(this.gettextCatalog.getString('Asiantuntija ei työskentele valitussa toimipisteessä <strong>"{{location}}"</strong>.', {
          location: this.location.name
        }))
      }
      if (p.isOffline) {
        const notAvailableText = this.gettextCatalog.getString('Asiantuntijalle ei voi varata aikaa verkossa palvelulle "{{service}}".', {
          service: this.service.name
        });
        const reserveByPhoneText = this.gettextCatalog.getString('Varaa aika soittamalla <a class="link-no-style" href="tel:+358104003400"><strong>010 400 3400</strong></a>.');
        p.mismatches.push(`${notAvailableText} ${reserveByPhoneText}`);
      }
    }

    if (this.location || this.service) {
      this.persons = this.persons.filter(person => this.location && person.hasLocation || this.service && (person.hasService || person.isOffline));
      this.fuseSearch = new Fuse(this.persons, FUSE_CONFIG);
    }

    this.updateSearches();

    this.updateGroups();
  }

  updateSearches() {
    // Update persons without changing the array reference
    this.filteredPersons.length = 0;
    const newPersons = this.filterPersons(this.persons);
    this.filteredPersons.push(...newPersons);
    this.updateGroups();
  }

  updateGroups() {
    this.personGroups = [
      {
        name: "onlinePersons",
        title: "",
        personnel: this.filteredPersons,
        showNotFound: true,
        maxDisplay: this.compactMode ? MAX_PERSONS : this.personFilterState.get(KEYS.MAX_DISPLAYS_ONLINE_PERSONS)
      }
    ];

    // Prepend favourite if it existed
    if (this.showFavourites) {
      // Make an explicit copy to prevent strange behaviour where filteredFavourites
      // is mutated when this.favouriteDoctors is.
      let filteredFavourites = this.favouriteDoctors.slice();
      filteredFavourites = locationFilter(filteredFavourites, this.locationFilterIds);
      filteredFavourites = serviceFilter(filteredFavourites, this.service != null ? this.service.id : null);

      this.personGroups.unshift({
        name: "favouritePersons",
        title: this.gettextCatalog.getString('Tutut asiantuntijat'),
        personnel: filteredFavourites,
        showNotFound: false,
        maxDisplay: this.personFilterState.get(KEYS.MAX_DISPLAYS_FAVOURITE_PERSONS),
      });
    }

    this.$log.debug("Final personGroups", this.personGroups);
  }

  /**
   * Is the user searching for persons serving service voucher patients?
   * @param {String[]} searchTerms
   * @returns {Boolean}
   */
  _checkSearchTermsForServiceVoucherMatches(searchTerms) {
    const searchString = searchTerms.join(' ');
    return searchString.match(SERVICE_VOUCHER_SEARCH_REGEX);
  }

  /**
   * Can the person be considered as he/she serves service voucher patients?
   * If the search was not about service voucher, returns true in all cases.
   * @param {Boolean} isServiceVoucherSearch
   * @param {Person} person
   * @returns {Boolean}
   */
  _filterServiceVoucherMatches(isServiceVoucherSearch, person) {
    return !isServiceVoucherSearch ||
      isServiceVoucherSearch && (
        person.serves_service_voucher_patients ||
        // This means that the person has 'palvelusetelipotilaat' or its translation in 'Palvelen mielelläni' section
        _.has(person.matches, 'group_names')
      )
  }

  filterPersons(persons) {
    const searchTerms = getSearchTerms(this.personSearchText);
    let searchResults = doSearch(searchTerms, this.fuseSearch);

    // If there were no search results and search was used, exit now as we don't need to show any persons
    // Just an error message with an empty person list
    if (searchTerms.length > 0 && Object.keys(searchResults).length === 0) {
      return persons.slice(0,0);
    }
    // Case 2 no search, limit display with other filters
    else if (searchTerms.length === 0) {
      persons = persons.filter(p => p.hasLocation && (p.hasService || p.isOffline));
      for (const person of persons) {
        person.score = person.isOffline * 0.2 + !person.hasLocation * 0.4 + !person.hasService * 0.3;
        person.matches = [];
      }
      if (this.jobTitleFilter.label !== this.gettextCatalog.getString(ALL_PERSONNELL)) {
        persons = persons.filter(person => person.job_title === this.jobTitleFilter.obj);
      }
      return persons.sort(this.sortPersonnel);
    }

    const isServiceVoucherSearch = this._checkSearchTermsForServiceVoucherMatches(searchTerms);

    const resultPersons = Object.keys(searchResults).map(personId => {
      // Persons are searched from set of the same persons so there has to be a match
      const person = persons.find(p=> p.id === parseInt(personId, 10));

      // Calculate final scores and show matches
      // Score personnel based on matches in service and location if set
      // Also increase score if the search was related to service voucher but the person doesn't serve patients with those
      const serviceVoucherScore = isServiceVoucherSearch ? !person.serves_service_voucher_patients * 0.5 : 0;
      person.score = (
        person.isOffline * 0.2 +
        !person.hasLocation * 0.4 +
        !person.hasService * 0.3 +
        serviceVoucherScore
      );

      const result = searchResults[person.id];
      person.score += result.score;
      person.matches = combineMatches(result.matches);

      // Add score (worsen result) for each complete search miss
      // If 3 terms are searched and person has 2 matches, they will get 1 penalty
      // This is the same score as getting complete mismatch from Fuse search
      person.score += searchTerms.length - result.matchCount;

      return person;
    });

    return resultPersons.sort(this.sortPersonnel)
      .filter((p) => this._filterServiceVoucherMatches(isServiceVoucherSearch, p));
  }

  sortPersonnel(l, r) {
    return l.score - r.score || (l.last_name + l.first_name).localeCompare(r.last_name + r.first_name, 'fi');
  }

  toggleFilters() {
    this.showMoreFilters = !this.showMoreFilters;
    this.personFilterState.save(KEYS.SHOW_FILTERS, this.showMoreFilters);
    if (this.showMoreFilters) {
      this.Analytics.postEvent('asiantuntijat', 'filter', 'auki');
    }
  }

  onSearchEnter() {
    this.ScrollService.navigateToId("personSearchInput", 75);
  }

  _setUser(user) {
    this.$log.debug("Person component got user", user);

    this.favouriteDoctors.length = 0;
    this.showFavourites = false;

    if (user != null) {
      this.showFavourites = true;
      const promises = [];

      if (user.personal_doctor != null) {
        promises.push(this.PersonResource.getOne(user.personal_doctor));
      }

      if (user.personal_hygienist != null) {
        promises.push(this.PersonResource.getOne(user.personal_hygienist));
      }

      return this.$q.all(promises)
      .then(resolves => {
        this.$log.debug("Resolved favourites", resolves);
        return resolves;
      })
      .then(resolves => {
        this.favouriteDoctors = this.setDetailTexts(resolves);
        this.updateGroups();
      });
    }
    else {
      this.$q.when([]);
    }
  }

  displayMore(personGroup) {
    personGroup.maxDisplay += DISPLAY_ADD;
    personGroup.maxDisplay = Math.min(personGroup.personnel.length, personGroup.maxDisplay);

    const path = `${KEYS.MAX_DISPLAYS}.${personGroup.name}`;
    this.personFilterState.save(path, personGroup.maxDisplay)
  }

  showPersonnel(personGroup) {
    personGroup.hidden = false;
  }
}

const config = {
  templateUrl: function($element, $attrs, $log) {
    // Direct templateUrl so that transclude works
    $log.debug('Determining Person component template to be used', $attrs.compactMode, $attrs.locationId, $attrs.serviceId);

    // Attributes are unevaluated so values are strings
    if ($attrs.compactMode === "true") {
      return personsCompactTpl;
    } else {
      return personsFullTpl;
    }
  },
  controller: PersonsComponent,
  transclude: true,
  bindings: {
    compactMode: '<', // A more compact layout if displayed inside the person modal
    personSelectedCallback: '&',
    serviceId: '<?',
    service: '<?',
    locationId: '<?',
    location: '<?',
    showInfo: '<?',
    personInfoCallback: '&?',
    activePerson: '<?',
    hideFiltersInFullMode: '<?',
  }
};

const mod = angular.module('app.persons', [
  angularSpinner,
    ngStorage,
    scrollServiceMod,
    authServiceMod,

    serviceCategoryRes,
    serviceGroupRes,
    serviceRes,
    locationRes,
    personConstants,
    normalButtonComponentMod,
    navButtonComponentMod,
    iconButtonComponentMod,
    generalCardComponentMod,
    personRes,
    personCardModule,
    jobTitleRes,
    jobtitleSelector,
    selectorMod,
    textfieldComponentMod,
    geolocationModule,
    flashMessageServiceMod,
    analyticsServiceMod,
])
.component('persons', config)
;

export default mod.name;



// WEBPACK FOOTER //
// ./src/app/persons/persons.component.js